import { Controller } from "@hotwired/stimulus"

// Mirrors the Subject Remotion component's position+scale on the
// wizard miniature live-preview while the user drags the
// SubjectSliderFieldsComponent sliders.
//
// Mounted on the wizard shell alongside per-template live-preview
// controllers. It listens for `input`/`change` events from sliders
// flagged with `data-live-preview-field="subject_position_y"` or
// `"subject_scale"` and writes CSS top/height on whatever element
// declares `data-subject-preview-target="subject"`.
//
// Templates without a subject preview target are no-ops — the
// controller can ride on the wizard shell unconditionally without
// touching every WizardContainer.
//
// Mapping rationale (the preview is a stylized 9:16 mini-frame, not a
// pixel-faithful Remotion render; the existing CSS used `width: 44%`
// for the default scale so we keep that ratio rather than literally
// mapping to canvas-height fractions, which would blow the subject up
// to ~98% of the mini-frame width):
//   - position_y in [0.30, 0.65] → CSS `top: position_y * 100%`,
//     consumed by the existing `translate(-50%, -50%)` centering.
//   - scale in [0.30, 0.90]      → CSS `width: scale * 100%` of the
//     preview frame. With the existing `aspect-ratio: 1/1`, height
//     auto-derives. Visually responsive; not pixel-accurate to render.
export default class extends Controller {
  static targets = ["subject"]

  connect() {
    this.handleEvent = this.handleEvent.bind(this)
    this.element.addEventListener("input", this.handleEvent)
    this.element.addEventListener("change", this.handleEvent)
    this.applyAll()
  }

  disconnect() {
    this.element.removeEventListener("input", this.handleEvent)
    this.element.removeEventListener("change", this.handleEvent)
  }

  handleEvent(event) {
    const field = event.target?.dataset?.livePreviewField
    if (field !== "subject_position_y" && field !== "subject_scale") return
    this.applyAll()
  }

  applyAll() {
    if (!this.hasSubjectTarget) return
    const el = this.subjectTarget
    // Prefer live slider input (Style step). Fall back to the saved
    // value embedded as a data attr on the subject element itself —
    // that's how downstream steps (Preview) keep the saved size+
    // position when no slider inputs are on the page.
    const positionY = this.fieldValue("subject_position_y") ?? this.dataAttrFloat(el, "subjectPositionY")
    const scale = this.fieldValue("subject_scale") ?? this.dataAttrFloat(el, "subjectScale")
    // Round to whole percent — keeps inspector tidy + dodges float
    // precision quirks (0.55 * 100 = 55.00000000000001).
    if (positionY != null) el.style.top = `${Math.round(positionY * 100)}%`
    if (scale != null) el.style.width = `${Math.round(scale * 100)}%`
  }

  fieldValue(name) {
    const el = this.element.querySelector(`[data-live-preview-field="${name}"]`)
    if (!el) return null
    const n = parseFloat(el.value)
    return Number.isFinite(n) ? n : null
  }

  dataAttrFloat(el, datasetKey) {
    const raw = el.dataset?.[datasetKey]
    if (raw == null) return null
    const n = parseFloat(raw)
    return Number.isFinite(n) ? n : null
  }
}
